"use client";
import Head from 'next/head';

const InstructionsPage = () => {
  return (
    <>
      <Head>
        <title>Overview of Epitope Prediction Methods - ASFV-EpiBase</title>
        <meta name="description" content="ASFV-EpiBase - Overview of Epitope Prediction Methods and computational tools documentation" />
      </Head>
      <div className="flex flex-col items-center p-6">
        {/* Overview of Epitope Prediction Methods Section */}
        <div className="w-full max-w-6xl">
          <div className="bg-white p-6 rounded-lg shadow-lg border">
            <h1 className="text-3xl font-bold text-gray-800 mb-6">Overview of Epitope Prediction Methods</h1>
            
            {/* Predicted Epitope Overview */}
            <div className="mb-8">
              <p className="text-gray-700 leading-relaxed">
                Predicted epitopes in this database were identified using a combination of established computational tools designed to capture different aspects of antigen recognition. For T-cell epitopes, both cytotoxic (CD8⁺) and helper (CD4⁺) predictions were performed using the NetMHCpan and NetMHCIIpan series, which evaluate peptide binding affinity and presentation likelihood to MHC class I and II molecules. For B-cell epitopes, multiple sequence- and structure-based algorithms were applied to assess surface accessibility, flexibility, hydrophilicity, and antigenic propensity. Together, these approaches provide a comprehensive in silico prediction framework to identify potential epitopes for experimental validation.
              </p>
            </div>

            {/* Cytotoxic T Cells */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Cytotoxic T Cells</h2>
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                  <h3 className="font-semibold text-blue-800 mb-2">NetMHCpan-4.1 EL</h3>
                  <p className="text-gray-700 text-sm">
                    Predicts MHC-I presented peptides using a neural network trained on eluted ligand and binding affinity data, providing likelihood scores that reflect natural antigen presentation. Higher scores indicate stronger presentation potential and higher likelihood of being a naturally processed epitope.
                  </p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                  <h3 className="font-semibold text-blue-800 mb-2">NetMHCpan-4.1 BA</h3>
                  <p className="text-gray-700 text-sm">
                    Estimates peptide–MHC-I binding affinity based on experimentally measured IC₅₀ values, reporting predicted binding strength and percentile ranks. Lower IC₅₀ values (or lower rank %) correspond to stronger MHC-I binding affinity.
                  </p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                  <h3 className="font-semibold text-blue-800 mb-2">TCRLens</h3>
                  <p className="text-gray-700 text-sm">
                    A structure-aware deep learning framework developed by our group that models residue-level interactions across five critical interface zones of peptide–MHC–TCR complexes using multi-scale graph representations and an equivariant graph neural network (EGNN). The model integrates a VAE–GAN to generate realistic negative samples and jointly predicts peptide–MHC binding, peptide–TCR recognition, and full-complex interactions. Higher prediction scores indicate stronger MHC-I binding affinity and higher confidence in TCR recognition potential.
                  </p>
                </div>
              </div>
            </div>

            {/* Helper T Cells */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Helper T Cells</h2>
              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                  <h3 className="font-semibold text-green-800 mb-2">NetMHCIIpan-4.3 EL</h3>
                  <p className="text-gray-700 text-sm">
                    Predicts peptides naturally presented by MHC-II molecules using deep learning trained on eluted ligand datasets, outputting likelihood scores for CD4⁺ T-cell recognition. Higher scores indicate stronger potential for antigen presentation and helper T-cell activation.
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                  <h3 className="font-semibold text-green-800 mb-2">NetMHCIIpan-4.3 BA</h3>
                  <p className="text-gray-700 text-sm">
                    Evaluates peptide–MHC-II binding affinity through neural network models trained on IC₅₀ data, returning predicted binding strength and ranking scores. Lower IC₅₀ or rank values signify tighter binding between the peptide and MHC-II molecule.
                  </p>
                </div>
              </div>
            </div>

            {/* B Cells */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">B Cells</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
                  <h3 className="font-semibold text-purple-800 mb-2">BepiPred 3.0</h3>
                  <p className="text-gray-700 text-sm">
                    Combines protein language models and structural features to predict linear and conformational B-cell epitopes, providing residue-level probability scores. Residues with higher probabilities (typically &gt;0.5) are more likely to be part of antibody-recognized epitopes.
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
                  <h3 className="font-semibold text-purple-800 mb-2">Chou–Fasman</h3>
                  <p className="text-gray-700 text-sm">
                    Identifies regions likely forming β-turns in protein sequences, which are often located on accessible surfaces and serve as potential B-cell epitopes. Residues with β-turn propensity values above threshold are predicted as potential epitope-forming regions.
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
                  <h3 className="font-semibold text-purple-800 mb-2">Emini</h3>
                  <p className="text-gray-700 text-sm">
                    Estimates surface accessibility of residues to identify exposed regions that are more likely to be recognized by antibodies. Higher accessibility scores indicate surface-exposed residues suitable for B-cell recognition.
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
                  <h3 className="font-semibold text-purple-800 mb-2">Karplus–Schulz</h3>
                  <p className="text-gray-700 text-sm">
                    Predicts local flexibility of amino acid residues based on B-factor values, highlighting flexible regions that tend to act as B-cell epitopes. Regions with higher flexibility scores suggest greater structural mobility and higher epitope potential.
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
                  <h3 className="font-semibold text-purple-800 mb-2">Kolaskar–Tongaonkar</h3>
                  <p className="text-gray-700 text-sm">
                    Uses physicochemical properties and antigenic propensity values of amino acids to identify antigenic determinants in protein sequences. Regions with average scores above 1.0 are generally considered antigenic and likely to elicit antibody response.
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
                  <h3 className="font-semibold text-purple-800 mb-2">Parker</h3>
                  <p className="text-gray-700 text-sm">
                    Calculates hydrophilicity profiles to pinpoint hydrophilic regions that are typically located on protein surfaces and recognized as B-cell epitopes. Higher hydrophilicity scores denote more exposed, water-facing residues with increased epitope likelihood.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
};

export default InstructionsPage;

